import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormsModule,
  ReactiveFormsModule,
  FormBuilder,
  FormGroup,
  Validators,
  AbstractControl,
} from '@angular/forms';
import { LucideAngularModule, Eye, EyeOff } from 'lucide-angular';
import { AuthService } from '../../../../core/services/auth.service';
import { SwalService } from '../../../../core/services/swal.service';

@Component({
  selector: 'app-change-password',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    LucideAngularModule,
  ],
  templateUrl: './change-password.component.html',
  styleUrls: ['./change-password.component.scss'],
})
export class ChangePasswordComponent {
  passwordForm: FormGroup;

  // Icônes Lucide
  readonly Eye = Eye;
  readonly EyeOff = EyeOff;

  // Propriétés pour gérer l'affichage des mots de passe
  showCurrentPassword = false;
  showNewPassword = false;
  showConfirmPassword = false;

  // Propriétés pour gérer l'état de chargement
  loading = false;

  constructor(
    private fb: FormBuilder,
    private authService: AuthService,
    private swalService: SwalService
  ) {
    this.passwordForm = this.fb.group(
      {
        currentPassword: ['', Validators.required],
        newPassword: ['', [Validators.required, Validators.minLength(8)]],
        confirmPassword: ['', Validators.required],
      },
      { validators: this.passwordMatchValidator }
    );
  }

  // Méthodes pour basculer l'affichage des mots de passe
  toggleCurrentPasswordVisibility(): void {
    this.showCurrentPassword = !this.showCurrentPassword;
  }

  toggleNewPasswordVisibility(): void {
    this.showNewPassword = !this.showNewPassword;
  }

  toggleConfirmPasswordVisibility(): void {
    this.showConfirmPassword = !this.showConfirmPassword;
  }

  passwordMatchValidator(
    control: AbstractControl
  ): { [key: string]: boolean } | null {
    const newPassword = control.get('newPassword');
    const confirmPassword = control.get('confirmPassword');

    if (
      newPassword &&
      confirmPassword &&
      newPassword.value !== confirmPassword.value
    ) {
      return { passwordMismatch: true };
    }
    return null;
  }

  onSubmit() {
    if (this.passwordForm.valid && !this.loading) {
      this.loading = true;

      const formData = this.passwordForm.value;

      this.authService
        .changeCurrentPassword({
          currentPassword: formData.currentPassword,
          newPassword: formData.newPassword,
        })
        .subscribe({
          next: (response) => {
            this.loading = false;
            this.swalService.success(
              'Succès',
              'Mot de passe modifié avec succès !'
            );
            this.passwordForm.reset();
            this.showCurrentPassword = false;
            this.showNewPassword = false;
            this.showConfirmPassword = false;
          },
          error: (error) => {
            this.loading = false;
            console.error('Erreur lors du changement de mot de passe:', error);

            if (error.status === 400) {
              this.swalService.error(
                'Erreur',
                "L'ancien mot de passe est incorrect"
              );
            } else if (error.status === 422) {
              this.swalService.error(
                'Erreur',
                'Le nouveau mot de passe ne respecte pas les critères de sécurité'
              );
            } else {
              this.swalService.error(
                'Erreur',
                'Une erreur est survenue lors du changement de mot de passe'
              );
            }
          },
        });
    }
  }

  get passwordMismatchError(): boolean {
    return (
      this.passwordForm.hasError('passwordMismatch') &&
      this.passwordForm.get('confirmPassword')?.touched === true
    );
  }
}
